import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ErrorMessage } from '../../../../components/common/ErrorMessage/ErrorMessage';
import { Pagination } from '../../../../components/common/Pagination';
import type { PaginationState } from '../../../../hooks/usePaginationState';

export interface HistoryColumn<T> {
  /** Translation key for the column header; also used as the React key. */
  headerKey: string;
  /** Fixed column width for the desktop table's colgroup, e.g. `'14%'`. */
  width: string;
  /** Applies the `numeric` class to both the header and body cells. */
  numeric?: boolean;
  renderCell: (item: T, t: TFunction) => ReactNode;
}

interface HistoryPanelProps<T> {
  items: T[];
  total: number;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  pagination: PaginationState;
  errorFallbackKey: string;
  loadingKey: string;
  emptyIcon: string;
  emptyTitleKey: string;
  emptySubtitleKey: string;
  columns: HistoryColumn<T>[];
  getRowKey: (item: T) => string;
  /** Must return an element with a `key` — rendered directly inside the cards list. */
  renderMobileCard: (item: T, t: TFunction) => ReactNode;
}

/**
 * Shared error -> loading -> empty -> (desktop table + mobile cards + pagination)
 * shell for the user history tabs. Sessions and transactions differ only in
 * their column definitions and mobile card layout.
 */
export function HistoryPanel<T>({
  items,
  total,
  isLoading,
  error,
  onRetry,
  pagination,
  errorFallbackKey,
  loadingKey,
  emptyIcon,
  emptyTitleKey,
  emptySubtitleKey,
  columns,
  getRowKey,
  renderMobileCard,
}: HistoryPanelProps<T>) {
  const { t } = useTranslation();

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : t(errorFallbackKey)}
        onRetry={onRetry}
      />
    );
  }

  if (isLoading) {
    return <div className="user-details-loading">{t(loadingKey)}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="user-details-empty">
        <div className="user-details-empty-icon">
          <span className="material-symbols-outlined" aria-hidden="true">{emptyIcon}</span>
        </div>
        <div className="user-details-empty-title">{t(emptyTitleKey)}</div>
        <div className="user-details-empty-subtitle">
          {t(emptySubtitleKey)}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="user-details-desktop-table">
        <table className="user-details-table">
          <colgroup>
            {columns.map((column) => (
              <col key={column.headerKey} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.headerKey} className={column.numeric ? 'numeric' : undefined}>
                  {t(column.headerKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={getRowKey(item)}>
                {columns.map((column) => (
                  <td key={column.headerKey} className={column.numeric ? 'numeric' : undefined}>
                    {column.renderCell(item, t)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="user-details-mobile-cards" style={{ padding: '0.875rem' }}>
        <div className="user-details-cards">
          {items.map((item) => renderMobileCard(item, t))}
        </div>
      </div>

      <Pagination
        total={total}
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        onPageChange={pagination.setPage}
        onRowsPerPageChange={pagination.setRowsPerPage}
      />
    </>
  );
}
